import { useQuery } from "react-query";
import { getModulesCatalog } from "../../../services/organizations/organizations";

export interface ModuleTab {
  key: string;
  label: string;
}

// Single source for the "All / Needs / Schemes / ..." tabs shown on both
// the Client Mapping and Manage Roles & Access list pages — driven by the
// same platform.modules catalog that powers the Add Organisation and Add
// Role screens' module checklists, so a new module only needs adding in
// one place (the catalog) instead of four separate hardcoded arrays.
//
// With an orgId, the tabs are scoped to that org's actual subscriptions
// (same as the Add/Edit Role module checklist) — a tenant shouldn't see
// tabs for modules it never subscribed to. Omit it (e.g. a Platform admin
// with no org selected, browsing "All organisations") to get the full
// catalog, unfiltered.
// Hidden from these two tab lists specifically (not removed from the
// catalog itself) — an org can still be subscribed to it via Add
// Organisation/Add Role, it just isn't offered as a Client Mapping /
// Manage Roles & Access tab.
const HIDDEN_TAB_LABELS = ["chatbot"];

export function useModuleTabs(orgId?: number | null) {
  const { data, isLoading } = useQuery(["organization-modules-catalog", orgId], async () => {
    let response = await getModulesCatalog(orgId);
    return (response?.data?.data?.list || []) as { key: string; name: string }[];
  });

  const tabs: ModuleTab[] = [
    { key: "all", label: "All" },
    ...(data || [])
      .filter((module) => !HIDDEN_TAB_LABELS.includes(module.name?.toLowerCase()))
      .map((module) => ({ key: module.key, label: module.name })),
  ];

  return { tabs, isLoading };
}
